import { useCallback, useState } from 'react';
import { Users } from 'lucide-react';
import { auditReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Dash, errMessage } from './ReportCentreKit';
import type { UserActivityReportDto, UserActivityRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'USER_ACTIVITY';

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'User',       header: 'User',            width: 24 },
  { key: 'Roles',      header: 'Roles',           width: 30 },
  { key: 'Created',    header: 'Created',         width: 10 },
  { key: 'Submitted',  header: 'Submitted',       width: 10 },
  { key: 'Approved',   header: 'Approved',        width: 10 },
  { key: 'Posted',     header: 'Posted',          width: 10 },
  { key: 'Reversed',   header: 'Reversed',        width: 10 },
  { key: 'Manual',     header: 'Manual',          width: 10 },
  { key: 'Automated',  header: 'Automated',       width: 10 },
  { key: 'Debit',      header: 'Total Debit',     width: 16, numFmt: '#,##0.00' },
  { key: 'Backdated',  header: 'Backdated',       width: 10 },
  { key: 'ClosedPd',   header: 'Closed-Period',   width: 12 },
  { key: 'Rejected',   header: 'Rejected',        width: 10 },
  { key: 'LastActive', header: 'Last Activity',   width: 20 },
];

function buildExportRows(rows: UserActivityRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    User:       r.userName,
    Roles:      (r.roleNames ?? []).join(', '),
    Created:    r.journalsCreated,
    Submitted:  r.submitted,
    Approved:   r.approved,
    Posted:     r.posted,
    Reversed:   r.reversed,
    Manual:     r.manualCount,
    Automated:  r.automatedCount,
    Debit:      r.totalDebit,
    Backdated:  r.backdatedCount,
    ClosedPd:   r.closedPeriodPostCount,
    Rejected:   r.rejectedCount,
    LastActive: r.lastActivityDate ?? '',
  }));
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Zero renders muted; a positive count in a risk column renders loud. */
const RiskCount = ({ value }: { value: number }) => (
  <span className={cn(
    'tabular-nums',
    value > 0 ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
  )}>
    {value}
  </span>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const UserActivityPage = () => {
  const { hasPermission } = usePermission();

  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState<UserActivityReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditReportApi.getUserActivity({ fromDate, toDate });
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  if (!hasPermission(Permissions.RPT_USER_ACTIVITY)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const users = data?.users ?? [];

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'User Activity',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(users),
      title: 'User Activity Matrix',
      subtitle: `${data.fromDate} to ${data.toDate}`,
    }, `UserActivity_${data.fromDate}_${data.toDate}`);
  };

  const handleCsv = () => {
    if (!data) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(users), `UserActivity_${data.fromDate}_${data.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">User Activity Matrix</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={users.length}
          filterSummary={`${fromDate}..${toDate}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <label className={LABEL_CLS}>From Date *</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
            </div>
            <div className="w-44">
              <label className={LABEL_CLS}>To Date *</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
            </div>
          </div>
          <RunReportButton onClick={() => { void run(); }} isLoading={loading} disabled={!fromDate || !toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to see per-user journal activity.' />
      )}

      {data && (
        <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Users</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
              {fmtDate(data.fromDate)} to {fmtDate(data.toDate)} · {users.length} users
            </span>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm border-collapse min-w-[1500px]">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                <tr>
                  <th className={TH_CLS}>User</th>
                  <th className={TH_CLS}>Roles</th>
                  <th className={TH_R_CLS}>Created</th>
                  <th className={TH_R_CLS}>Submitted</th>
                  <th className={TH_R_CLS}>Approved</th>
                  <th className={TH_R_CLS}>Posted</th>
                  <th className={TH_R_CLS}>Reversed</th>
                  <th className={TH_R_CLS} title="Manual / Automated (of created)">Man / Auto</th>
                  <th className={TH_R_CLS}>Total Debit</th>
                  <th className={TH_R_CLS} title="Journals created after the posting date they claim">Backdated</th>
                  <th className={TH_R_CLS} title="Posts into closed periods">Closed-Pd</th>
                  <th className={TH_R_CLS}>Rejected</th>
                  <th className={cn(TH_CLS, 'w-40')}>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                      No user activity in the selected window.
                    </td>
                  </tr>
                ) : users.map(u => (
                  <tr key={u.userId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{u.userName}</td>
                    <td className="px-3 py-2">
                      {u.roleNames && u.roleNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.roleNames.map(role => (
                            <span key={role} className="inline-block px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[10px] font-medium whitespace-nowrap">
                              {role}
                            </span>
                          ))}
                        </div>
                      ) : <Dash />}
                    </td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{u.journalsCreated}</td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{u.submitted}</td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{u.approved}</td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{u.posted}</td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{u.reversed}</td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                      {u.manualCount} / {u.automatedCount}
                    </td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(u.totalDebit)}</td>
                    <td className={AMT_CLS}><RiskCount value={u.backdatedCount} /></td>
                    <td className={AMT_CLS}><RiskCount value={u.closedPeriodPostCount} /></td>
                    <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{u.rejectedCount}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(u.lastActivityDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
